/**
 * CostosModal — catálogo de costos de productos para el balance de obras.
 *
 * Importa el Excel de Finnegans (Costos_Prods_Finn.xlsx, hoja con columnas
 * Nombre | Codigo | CostoStandard | PrecioBaseVenta) a la colección
 * obras_costos. Solo lo ven usuarios con obrasFinanzas (y admin).
 */

import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { importarCostosObras, getCostosResumen } from "../services/obrasFS";

function toDate(ts) {
  if (!ts) return null;
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return null;
}

/** Lee el Excel y devuelve filas { codigo, nombre, costo, precioVenta } */
function parseExcelCostos(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (rows.length < 2) return [];

  // Ubicar columnas por nombre de encabezado (tolerante a mayúsculas/orden)
  const headers = rows[0].map(h => String(h || "").toLowerCase().trim());
  const col = (busca) => headers.findIndex(h => h.includes(busca));
  const iCodigo = col("codigo");
  const iNombre = col("nombre");
  const iCosto = col("costo");
  const iPrecio = col("precio");
  if (iCodigo < 0 || iCosto < 0) {
    throw new Error('El archivo no tiene las columnas esperadas ("Codigo" y "CostoStandard")');
  }

  return rows.slice(1)
    .map(r => ({
      codigo: String(r[iCodigo] ?? "").trim(),
      nombre: iNombre >= 0 ? String(r[iNombre] ?? "").trim() : null,
      costo: Number(r[iCosto]),
      precioVenta: iPrecio >= 0 ? Number(r[iPrecio]) : null,
    }))
    .filter(f => f.codigo && Number.isFinite(f.costo));
}

export default function CostosModal({ user, onClose }) {
  const [resumen, setResumen] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, texto }
  const fileRef = useRef(null);

  useEffect(() => {
    getCostosResumen().then(setResumen).catch(() => setResumen(null));
  }, []);

  async function handleArchivo(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const filas = parseExcelCostos(await file.arrayBuffer());
      if (filas.length === 0) throw new Error("No se encontraron productos en el archivo");
      const { importados } = await importarCostosObras(filas, user, file.name);
      setMsg({ ok: true, texto: `✅ Se importaron ${importados} productos con su costo` });
      setResumen(await getCostosResumen());
    } catch (err) {
      setMsg({ ok: false, texto: "No se pudo importar: " + (err.message || err) });
    } finally {
      setBusy(false);
    }
  }

  const fecha = toDate(resumen?.actualizadoAt);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 270, padding: 16,
        overflowY: "auto",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 20, width: "100%", maxWidth: 460, margin: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>💲 Costos de productos</h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>

        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>
          Catálogo que usa el balance para valorizar los materiales de cada obra.
          Se importa desde el Excel de Finnegans (<b>Costos_Prods_Finn.xlsx</b> —
          columnas Nombre, Codigo, CostoStandard, PrecioBaseVenta). Reimportá el
          archivo cada vez que cambien los costos.
        </p>

        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 12 }}>
          {resumen === undefined ? "Cargando…"
            : resumen === null ? "Todavía no se importaron costos."
            : <>
                <b>{resumen.cantidad}</b> productos cargados
                {fecha ? ` · última importación ${fecha.toLocaleDateString("es-UY")} ` : " "}
                {resumen.porNombre ? `por ${resumen.porNombre}` : ""}
                {resumen.archivo ? <div style={{ color: "#94a3b8", fontSize: 12 }}>{resumen.archivo}</div> : null}
              </>}
        </div>

        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleArchivo} />
        <button className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? "Importando…" : "📥 Importar Excel de costos"}
        </button>

        {msg && (
          <div style={{
            marginTop: 10, fontSize: 13, padding: "8px 10px", borderRadius: 8,
            background: msg.ok ? "#f0fdf4" : "#fee2e2",
            color: msg.ok ? "#15803d" : "#b91c1c",
          }}>
            {msg.texto}
          </div>
        )}
      </div>
    </div>
  );
}
